import { useEffect, useRef, useState } from "react";

/**
 * Rolling numerals for a metric whose value changes while on screen.
 *
 * Nothing rolls on arrival: macOS shows data at its settled value, and a digit
 * column travelling up from zero renders a wrong number the whole way.
 *
 * Each digit is a column of 0-9 inside a clipped box, moved by translateY. That
 * matters for two reasons: transform is compositor-only, so a value change
 * cannot reflow the page or contribute to CLS; and the box never changes size,
 * so neighbouring text does not move while the number settles.
 *
 * Digits are rendered in tabular figures so every column is the same width —
 * without that, a 1 rolling to an 8 would widen the number mid-flight and shove
 * everything after it sideways.
 *
 * Only digits animate. Separators, currency marks and signs are static text, so
 * "13,713,051" rolls its numerals while its commas stay put.
 */
const DIGITS = ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9"];

function Digit({ value, delay }: { value: number; delay: number }) {
  // First paint is the real value. Only a change that happens while mounted
  // rolls, which is a genuine state transition rather than an arrival effect.
  const [shown, setShown] = useState(value);
  const mounted = useRef(false);
  const frame = useRef<number | null>(null);

  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true;
      return;
    }
    frame.current = requestAnimationFrame(() => setShown(value));
    return () => {
      if (frame.current != null) cancelAnimationFrame(frame.current);
    };
  }, [value]);

  return (
    <span
      aria-hidden
      style={{
        display: "inline-block", overflow: "hidden",
        height: "1em", lineHeight: "1em", verticalAlign: "baseline",
      }}
    >
      <span
        style={{
          display: "flex", flexDirection: "column",
          transform: `translateY(-${shown}em)`,
          transition: `transform 620ms cubic-bezier(.22,1,.36,1) ${delay}ms`,
        }}
      >
        {DIGITS.map((d) => (
          <span key={d} style={{ height: "1em", lineHeight: "1em" }}>{d}</span>
        ))}
      </span>
    </span>
  );
}

export function NumberRoll({ value, format }: {
  value: number;
  /** Defaults to thousands-separated integers. */
  format?: (v: number) => string;
}) {
  const text = (format ?? ((v: number) => Math.round(v).toLocaleString()))(value);

  // Reduced motion gets the settled number immediately — not a slower roll.
  const still = typeof window !== "undefined"
    && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  if (still) return <span className="tabnum">{text}</span>;

  const chars = text.split("");
  // Later digits start fractionally after earlier ones, so the number settles
  // left-to-right instead of every column landing at once.
  let digitIndex = 0;

  return (
    <span className="tabnum" style={{ display: "inline-flex", alignItems: "baseline" }}>
      {/* The real value for anyone not watching the animation. */}
      <span className="ui-sr-only">{text}</span>
      {chars.map((ch, i) => {
        if (ch >= "0" && ch <= "9") {
          const d = <Digit key={i} value={Number(ch)} delay={digitIndex * 26} />;
          digitIndex += 1;
          return d;
        }
        return <span key={i} aria-hidden>{ch}</span>;
      })}
    </span>
  );
}
